import type { RefObject } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import clsx from 'clsx'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLEStaff, selectLoggedIn } from 'selectors/accountSelectors'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import TextButton from 'LuxKit/Button/TextButton'
import { useItineraryContext } from './ChatTripPlannerPage/contexts/ItineraryContext'
import { useCartButtonSummary } from './ChatTripPlannerPage/hooks/useCartButtonSummary'
import { useFetchCartPanelOffers } from './ChatTripPlannerPage/hooks/useFetchCartPanelOffers'
import { useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import CartButton from './ChatTripPlannerPage/components/CartButton'
import DownloadItineraryButton from './ChatTripPlannerPage/components/DownloadItineraryButton'
import LineBarsIcon from 'LuxKit/Icons/line/LineBarsIcon'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import useHasMounted from 'hooks/useHasMounted'
import {
  TripPlannerStickyHeader,
  TripPlannerHeaderIconButton,
  LeftContent,
  RightContent,
} from 'tripPlanner/components/Header/Shared'
import TripPlannerHomeLogoLink from 'tripPlanner/components/Header/TripPlannerHomeLogoLink'
import { useTripPlannerMenuDrawer } from './ChatTripPlannerPage/hooks/useTripPlannerMenuDrawer'
import { useTripPlannerNavigation } from './ChatTripPlannerPage/hooks/useTripPlannerNavigation'
import TripPlannerDrawer from './TripPlannerDrawer'
import { TRIP_PLANNER_HEADER_HEIGHT, TRIP_PLANNER_SIDE_NAV_ID } from './tripPlannerNavigationConstants'

const NavigationControls = styled(Group)``

const HeaderLayout = styled(TripPlannerStickyHeader)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-rows: minmax(0, 1fr);
  grid-template-areas: 'navigation actions';
  column-gap: ${rem(8)};
  block-size: ${rem(TRIP_PLANNER_HEADER_HEIGHT)};
  padding-block: ${rem(4)};
  flex-shrink: 0;
  border-block-end: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};

  @media print {
    display: none;
  }

  ${mediaQueryUp.desktop} {
    padding-inline-end: ${rem(32)};

    &.side-nav-expanded {
      border-block-end-color: transparent;
    }

    &.side-nav-expanded ${NavigationControls} {
      display: none;
    }

    &.side-nav-expanded.without-actions {
      display: none;
    }
  }
`

const HeaderNavigation = styled(LeftContent)`
  grid-area: navigation;
`

const HeaderActions = styled(RightContent)`
  grid-area: actions;

  margin-inline-start: 0;
`

interface Props {
  chatId: number | undefined
  onStartNewChat: () => void
  onChatSelect: (chatId: number) => void
  onChatDeleted: (deletedChatId: number, fallbackChatId: number | undefined) => void
  isDevMode: boolean
  isRewindMode: boolean
  onDevModeToggle: () => void
  onRewindModeToggle: () => void
  isLandingView: boolean
  isSideNavExpanded: boolean
  onExpandSideNav: () => void
  menuButtonRef: RefObject<HTMLButtonElement | null>
}

function TripPlannerHeader({
  chatId,
  onStartNewChat,
  onChatSelect,
  onChatDeleted,
  isDevMode,
  isRewindMode,
  onDevModeToggle,
  onRewindModeToggle,
  isLandingView,
  isSideNavExpanded,
  onExpandSideNav,
  menuButtonRef,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  // ── Own data reads ──

  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isStaff = useAppSelector(isLEStaff)
  const isDesktopUp = useScreenSizeUp('desktop')
  // The server can't know the viewport, so the side-nav ARIA wiring waits for mount to keep hydration stable.
  const hasMounted = useHasMounted()
  const controlsSideNav = isLandingView && isDesktopUp
  const describesSideNav = hasMounted && controlsSideNav
  const eventBus = useTripPlannerEventBus()
  const { itineraryData, hasItineraryData } = useItineraryContext()
  useFetchCartPanelOffers(itineraryData, true)
  const cartSummary = useCartButtonSummary(itineraryData, isLoggedIn)

  const handleCartClick = useCallback(() => {
    eventBus.dispatch({ type: 'open_cart_panel' })
  }, [eventBus])

  const navigation = useTripPlannerNavigation({
    isLoggedIn,
    onChatDeleted,
    activeChatId: chatId,
    hasActiveChatItinerary: hasItineraryData,
  })
  const { isMenuOpen, openMenu, closeMenu, closeMenuOnNavigate, onMenuAfterClose } = useTripPlannerMenuDrawer({ openerRef: menuButtonRef })

  const handleMenuClick = useCallback(() => {
    if (controlsSideNav) {
      onExpandSideNav()
      return
    }

    openMenu()
  }, [controlsSideNav, onExpandSideNav, openMenu])

  const handleLogin = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  // ── i18n ──

  const loginButtonText = intl.formatMessage({
    id: 'ARcYTJ2nmHAv35KNSneu',
    defaultMessage: 'Log in to save your trip',
    description: 'Button text prompting unauthenticated users to log in to save their trip plan',
  })

  const openMenuAriaLabel = intl.formatMessage({
    id: 'sIGePGauk8KDHGHEBhex',
    defaultMessage: 'Open menu',
    description: 'Accessible label for the hamburger menu button in the TripPlanner header bar',
  })

  const showCartButton = !!itineraryData
  const showLoginButton = !isLoggedIn
  const showDownloadButton = hasItineraryData
  const hasActions = showCartButton || showLoginButton || showDownloadButton

  const rightContent = useMemo(() => (
    <Group direction="horizontal" gap={8} verticalAlign="center">
      {showCartButton && <CartButton
        itemCount={cartSummary.bookableItemCount}
        totalPrice={cartSummary.estimatedTotal}
        isLoading={cartSummary.isLoading}
        onClick={handleCartClick}
      />}
      {showLoginButton && (
        <TextButton kind="secondary" size="medium" onClick={handleLogin}>
          {loginButtonText}
        </TextButton>
      )}
      {showDownloadButton && <DownloadItineraryButton chatId={chatId} tripActions />}
    </Group>
  ), [showLoginButton, showDownloadButton, chatId, handleLogin, loginButtonText, showCartButton, cartSummary.bookableItemCount, cartSummary.estimatedTotal, cartSummary.isLoading, handleCartClick])

  return (
    <>
      <HeaderLayout className={clsx({ 'side-nav-expanded': isSideNavExpanded, 'without-actions': !hasActions })}>
        <HeaderNavigation>
          <NavigationControls
            direction="horizontal"
            gap={8}
            verticalAlign="center"
          >
            <TripPlannerHeaderIconButton
              ref={menuButtonRef}
              onClick={handleMenuClick}
              aria-label={openMenuAriaLabel}
              aria-expanded={describesSideNav ? isSideNavExpanded : undefined}
              aria-controls={describesSideNav ? TRIP_PLANNER_SIDE_NAV_ID : undefined}
            >
              <LineBarsIcon />
            </TripPlannerHeaderIconButton>
            {isLandingView && <TripPlannerHomeLogoLink />}
          </NavigationControls>
        </HeaderNavigation>
        {hasActions && (
          <HeaderActions>
            {rightContent}
          </HeaderActions>
        )}
      </HeaderLayout>
      <TripPlannerDrawer
        {...navigation}
        isOpen={isMenuOpen}
        onClose={closeMenu}
        onNavigate={closeMenuOnNavigate}
        onAfterClose={onMenuAfterClose}
        isLoggedIn={isLoggedIn}
        isLandingView={isLandingView}
        onStartNewChat={onStartNewChat}
        onChatSelect={onChatSelect}
        activeChatId={chatId}
        isStaff={isStaff}
        isDevMode={isDevMode}
        onDevModeToggle={onDevModeToggle}
        isRewindMode={isRewindMode}
        onRewindModeToggle={onRewindModeToggle}
      />
    </>
  )
}

export default TripPlannerHeader
